"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignallingServerConnection = void 0;
const logging_1 = require("../../util/logging");
const LinkupAddress_1 = require("./LinkupAddress");
class SignallingServerConnection {
    constructor(serverURL) {
        if (!SignallingServerConnection.isWebRTCBased(serverURL)) {
            throw new Error('LinkupServerConnection expects a URL that starts with "' + SignallingServerConnection.WRTC_URL_PREFIX + '", bailing out.');
        }
        this.serverURL = serverURL;
        this.ws = null;
        this.rawMessageCallbacks = new Map();
        this.newCallMessageCallbacks = new Map();
        this.messageCallbacks = new Map();
        this.linkupIdsToListen = new Set();
        this.messageQueue = [];
        this.checkWebsocket();
    }
    static isWebRTCBased(serverURL) {
        return serverURL.startsWith(SignallingServerConnection.WRTC_URL_PREFIX);
    }
    static getRealServerURL(serverURL) {
        return serverURL.slice(SignallingServerConnection.WRTC_URL_PREFIX.length);
    }
    listenForMessagesNewCall(recipient, callback) {
        if (recipient.serverURL !== this.serverURL) {
            let e = new Error('Trying to listen for calls to ' +
                recipient.serverURL +
                ' but this is a connection to ' +
                this.serverURL);
            SignallingServerConnection.logger.error(e);
            throw e;
        }
        let recipientCallCallbacks = this.newCallMessageCallbacks.get(recipient.linkupId);
        if (recipientCallCallbacks === undefined) {
            recipientCallCallbacks = new Set();
            this.newCallMessageCallbacks.set(recipient.linkupId, recipientCallCallbacks);
        }
        recipientCallCallbacks.add(callback);
        this.setUpListenerIfNew(recipient.linkupId);
    }
    listenForRawMessages(recipient, callback) {
        if (recipient.serverURL !== this.serverURL) {
            let e = new Error('Trying to listen for raw messages to server ' +
                recipient.serverURL +
                ' but this is a connection to ' +
                this.serverURL);
            SignallingServerConnection.logger.error(e);
            throw e;
        }
        let recipientRawCallbacks = this.rawMessageCallbacks.get(recipient.linkupId);
        if (recipientRawCallbacks === undefined) {
            recipientRawCallbacks = new Set();
            this.rawMessageCallbacks.set(recipient.linkupId, recipientRawCallbacks);
        }
        recipientRawCallbacks.add(callback);
        this.setUpListenerIfNew(recipient.linkupId);
    }
    listenForMessagesOnCall(recipient, callId, callback) {
        if (recipient.serverURL !== this.serverURL) {
            let e = new Error('Trying to listen for messages to ' +
                recipient.serverURL +
                ' but this is a connection to ' +
                this.serverURL);
            SignallingServerConnection.logger.error(e);
            throw e;
        }
        let linkupIdCalls = this.messageCallbacks.get(recipient.linkupId);
        if (linkupIdCalls === undefined) {
            linkupIdCalls = new Map();
            this.messageCallbacks.set(recipient.linkupId, linkupIdCalls);
        }
        let messageCallbacks = linkupIdCalls.get(callId);
        if (messageCallbacks === undefined) {
            messageCallbacks = new Set();
            linkupIdCalls.set(callId, messageCallbacks);
        }
        messageCallbacks.add(callback);
        this.setUpListenerIfNew(recipient.linkupId);
    }
    listenForLinkupAddressQueries(callback) {
        this.listeningAddressesQueryCallback = callback;
    }
    sendMessage(sender, recipient, callId, data) {
        if (recipient.serverURL !== this.serverURL) {
            let e = new Error('Trying to send a linkup message to ' +
                recipient.serverURL +
                ' but this is a connection to ' +
                this.serverURL);
            SignallingServerConnection.logger.error(e);
            throw e;
        }
        var message = {
            'action': 'send',
            'linkupId': recipient.linkupId,
            'callId': callId,
            'data': data,
            'replyServerUrl': sender.serverURL,
            'replyLinkupId': sender.linkupId,
        };
        this.enqueueAndSend(JSON.stringify(message));
    }
    sendRawMessage(sender, recipient, data, sendLimit) {
        if (recipient.serverURL !== this.serverURL) {
            let e = new Error('Trying to send a linkup message to ' +
                recipient.serverURL +
                ' but this is a connection to ' +
                this.serverURL);
            SignallingServerConnection.logger.error(e);
            throw e;
        }
        var message = {
            'action': 'send',
            'linkupId': recipient.linkupId,
            'raw': 'true',
            'data': data,
            'replyServerUrl': sender.serverURL,
            'replyLinkupId': sender.linkupId,
        };
        if (sendLimit !== undefined) {
            message['limit'] = sendLimit;
        }
        this.enqueueAndSend(JSON.stringify(message));
    }
    sendListeningAddressesQuery(queryId, addresses) {
        let linkupIds = new Array();
        for (const address of addresses) {
            if (address.serverURL !== this.serverURL) {
                let e = new Error('Trying to send an address query for ' +
                    address.serverURL +
                    ' but this is a connection to ' +
                    this.serverURL);
                SignallingServerConnection.logger.error(e);
                throw e;
            }
            linkupIds.push(address.linkupId);
        }
        var message = {
            'action': 'query',
            'linkupIds': linkupIds,
            'queryId': queryId
        };
        this.enqueueAndSend(JSON.stringify(message));
    }
    checkWebsocket() {
        if (this.ws !== null && this.ws.readyState === WebSocket.OPEN) {
            return true;
        }
        else {
            if (this.ws === null ||
                (this.ws.readyState === WebSocket.CLOSING ||
                    this.ws.readyState === WebSocket.CLOSED)) {
                SignallingServerConnection.logger.debug('creating websocket to server ' + this.serverURL);
                this.ws = new WebSocket(SignallingServerConnection.getRealServerURL(this.serverURL));
                this.ws.onmessage = (ev) => {
                    const message = JSON.parse(ev.data);
                    const ws = this.ws;
                    if (message['action'] === 'ping') {
                        SignallingServerConnection.logger.trace('sending pong to ' + this.serverURL);
                        ws.send(JSON.stringify({ 'action': 'pong' }));
                    }
                    else if (message['action'] === 'send') {
                        const linkupId = message['linkupId'];
                        const callId = message['callId'];
                        const raw = message['raw'];
                        if (callId !== undefined) {
                            const linkupIdCalls = this.messageCallbacks.get(linkupId);
                            let found = false;
                            if (linkupIdCalls !== undefined) {
                                let callMessageCallbacks = linkupIdCalls.get(callId);
                                if (callMessageCallbacks !== undefined) {
                                    callMessageCallbacks.forEach((callback) => {
                                        SignallingServerConnection.logger.debug('Delivering linkup message to ' + linkupId + ' on call ' + message['callId']);
                                        callback(message['data']);
                                        found = true;
                                    });
                                }
                            }
                            if (!found) {
                                found = false;
                                let linkupIdCallbacks = this.newCallMessageCallbacks.get(linkupId);
                                if (linkupIdCallbacks !== undefined) {
                                    linkupIdCallbacks.forEach((callback) => {
                                        SignallingServerConnection.logger.debug('Calling default callback for linkupId ' + linkupId + ', unlistened callId is ' + callId);
                                        callback(new LinkupAddress_1.LinkupAddress(message['replyServerUrl'], message['replyLinkupId']), new LinkupAddress_1.LinkupAddress(this.serverURL, linkupId), callId, message['data']);
                                        found = true;
                                    });
                                }
                                if (!found) {
                                    SignallingServerConnection.logger.warning('Received message for unlistened linkupId: ' + linkupId, message);
                                }
                            }
                        }
                        else if (raw !== undefined && raw === 'true') {
                            let callbacks = this.rawMessageCallbacks.get(linkupId);
                            if (callbacks !== undefined) {
                                callbacks.forEach((callback) => {
                                    SignallingServerConnection.logger.debug('Calling raw message callback for linkupId ' + linkupId);
                                    callback(new LinkupAddress_1.LinkupAddress(message['replyServerUrl'], message['replyLinkupId']), new LinkupAddress_1.LinkupAddress(this.serverURL, linkupId), message['data']);
                                });
                            }
                        }
                    }
                    else if (message['action'] === 'query-reply') {
                        const queryId = message['queryId'];
                        const hits = message['hits'];
                        let callback = this.listeningAddressesQueryCallback;
                        if (callback !== undefined) {
                            let matchingLinkupAddresses = new Array();
                            for (const linkupId of hits) {
                                matchingLinkupAddresses.push(new LinkupAddress_1.LinkupAddress(this.serverURL, linkupId));
                            }
                            callback(queryId, matchingLinkupAddresses);
                        }
                    }
                    else {
                        SignallingServerConnection.logger.info('received unknown message on ' + this.serverURL + ': ' + ev.data);
                    }
                };
                this.ws.onopen = () => {
                    SignallingServerConnection.logger.debug('done creating websocket to URL ' + this.serverURL);
                    this.setUpListeners();
                    this.emptyMessageQueue();
                };
            }
            return false;
        }
    }
    setUpListeners() {
        for (let linkupId of this.linkupIdsToListen) {
            this.setUpListener(linkupId);
        }
    }
    setUpListenerIfNew(linkupId) {
        if (!this.linkupIdsToListen.has(linkupId)) {
            this.setUpListener(linkupId);
            this.linkupIdsToListen.add(linkupId);
        }
    }
    // Notice this function is idempotent
    setUpListener(linkupId) {
        // check if we need to send a LISTEN message
        if (this.ws !== null && this.ws.readyState === this.ws.OPEN) {
            this.ws.send(JSON.stringify({ 'action': 'listen', 'linkupId': linkupId }));
            SignallingServerConnection.logger.debug('sending listen command through websocket for linkupId ' + linkupId);
        }
    }
    emptyMessageQueue() {
        if (this.checkWebsocket()) {
            SignallingServerConnection.logger.debug('about to empty message queue to ' +
                this.serverURL + ' (' + this.messageQueue.length +
                ' messages to send)');
            while (this.messageQueue.length > 0) {
                let message = this.messageQueue.shift();
                let ws = this.ws;
                SignallingServerConnection.logger.trace('about to send this to ' + this.serverURL);
                SignallingServerConnection.logger.trace(message);
                ws.send(message);
            }
        }
    }
    enqueueAndSend(message) {
        this.messageQueue.push(message);
        this.emptyMessageQueue();
    }
    close() {
        var _a;
        (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
    }
}
exports.SignallingServerConnection = SignallingServerConnection;
SignallingServerConnection.logger = new logging_1.Logger(SignallingServerConnection.name, logging_1.LogLevel.ERROR);
SignallingServerConnection.WRTC_URL_PREFIX = 'wrtc+';
//# sourceMappingURL=SignallingServerConnection.js.map